import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTasks,
  countTasks,
  createTask,
  listSubtasks,
  updateTask,
  deleteTask,
  deleteTasks,
  listHappenings,
  listUsers,
  checkTaskDependencies,
  checkBulkDependencies,
  getTaskReminders,
  createTaskReminder,
  deleteTaskReminder,
  deleteTaskReminders,
  type Task,
  type TaskCreate,
  type TaskUpdate,
  type TaskType,
  type TaskStatus,
  type TaskPriority,
  type Happening,
  type User,
  type DependencyCheckResult,
  type TaskReminder,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { AsyncSearchableSelect } from '@/components/AsyncSearchableSelect';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { KanbanBoard } from '@/components/KanbanBoard';
import { TasksCalendar } from '@/components/TasksCalendar';
import { type VisibleRange } from '@/components/Calendar';
import { useViewStore } from '@/lib/viewStore';
import { useFilterStore, type KanbanSortKey, type SortDirection, type ColumnSort } from '@/lib/filterStore';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { DangerZone } from '@/components/DangerZone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronDown, ChevronRight, CornerDownRight, Plus, Link as LinkIcon, CheckCircle2, Pencil, X, User as UserIcon, Calendar, Trash2, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Search, Clock, AlertTriangle } from 'lucide-react';

const TASK_TYPES: TaskType[] = ['booking', 'preparation', 'follow_up', 'technical'];
const TASK_STATUSES: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done'];
const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

const coerceTaskType = (value: FormDataEntryValue | null): TaskType => {
  if (typeof value === 'string' && TASK_TYPES.includes(value as TaskType)) {
    return value as TaskType;
  }
  return 'booking';
};

const coerceTaskStatus = (value: FormDataEntryValue | null): TaskStatus => {
  if (typeof value === 'string' && TASK_STATUSES.includes(value as TaskStatus)) {
    return value as TaskStatus;
  }
  return 'todo';
};

const coerceTaskPriority = (value: FormDataEntryValue | null): TaskPriority => {
  if (typeof value === 'string' && TASK_PRIORITIES.includes(value as TaskPriority)) {
    return value as TaskPriority;
  }
  return 'medium';
};

const getTaskTypeLabel = (type: TaskType): string => {
  const labels: Record<TaskType, string> = {
    booking: 'Booking',
    preparation: 'Preparation',
    follow_up: 'Follow Up',
    technical: 'Technical',
  };
  return labels[type] || type;
};

const getStatusBadgeColor = (status: TaskStatus): string => {
  switch (status) {
    case 'done': return 'bg-green-badge text-green-badge-foreground';
    case 'in_progress': return 'bg-blue-badge text-blue-badge-foreground';
    case 'blocked': return 'bg-red-badge text-red-badge-foreground';
    case 'todo': return 'bg-muted text-muted-foreground';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getPriorityBadgeColor = (priority: TaskPriority): string => {
  switch (priority) {
    case 'urgent': return 'bg-red-badge text-red-badge-foreground';
    case 'high': return 'bg-orange-badge text-orange-badge-foreground';
    case 'medium': return 'bg-yellow-badge text-yellow-badge-foreground';
    case 'low': return 'bg-muted text-muted-foreground';
    default: return 'bg-muted text-muted-foreground';
  }
};

// Task with subtasks grouped
type TaskWithSubtasks = Task & { subtasks?: Task[] };

// Compact per-column sort control rendered inside each kanban column header.
// Two pieces: a select for which field to sort by (none = "default" order),
// and a small arrow button to flip direction. The arrow is hidden when sort
// is "default" since direction is meaningless there.
interface ColumnSortControlProps {
  sort: ColumnSort | undefined;
  onChange: (patch: Partial<ColumnSort>) => void;
}

function ColumnSortControl({ sort, onChange }: ColumnSortControlProps) {
  const sortBy = sort?.sortBy ?? 'default';
  const sortDir = sort?.sortDir ?? 'asc';
  return (
    <div className="flex items-center gap-1">
      <select
        value={sortBy}
        onChange={(e) => onChange({ sortBy: e.target.value as KanbanSortKey })}
        className="h-6 rounded-md border border-input bg-background px-1.5 text-xs"
        title="Sort this column by"
        onClick={(e) => e.stopPropagation()}
      >
        <option value="default">—</option>
        <option value="priority">Priority</option>
        <option value="due_date">Due date</option>
        <option value="title">Title</option>
        <option value="created">Created</option>
      </select>
      {sortBy !== 'default' && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-input bg-background hover:bg-accent"
          title={sortDir === 'asc' ? 'Ascending — click to flip' : 'Descending — click to flip'}
          aria-label={`Sort direction: ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
        >
          {sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

// Assignee filter dropdown component with searchable users
interface AssigneeFilterDropdownProps {
  assigneeFilter: string;
  onFilterChange: (value: string) => void;
  linkedUsers: Array<{ id: string; name: string }>;
  currentUserId?: string;
}

function AssigneeFilterDropdown({
  assigneeFilter,
  onFilterChange,
  linkedUsers,
  currentUserId,
}: AssigneeFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get display label for current filter
  const getFilterLabel = () => {
    if (assigneeFilter === 'all') return 'All Tasks';
    if (assigneeFilter === 'me') return 'My Tasks';
    const user = linkedUsers.find(u => u.id === assigneeFilter);
    return user ? `${user.name}'s Tasks` : 'Filter by user...';
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return linkedUsers;
    const query = searchQuery.toLowerCase();
    return linkedUsers.filter(u => u.name.toLowerCase().includes(query));
  }, [linkedUsers, searchQuery]);

  const handleSelect = (value: string) => {
    onFilterChange(value);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm min-w-[200px] hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          assigneeFilter !== 'all'
            ? 'border-primary bg-primary/5 font-medium'
            : 'border-input bg-background'
        )}
      >
        <span className="truncate">{getFilterLabel()}</span>
        <ArrowDown className={cn('h-4 w-4 transition-transform ml-2 flex-shrink-0', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[300px] rounded-md border bg-popover shadow-lg">
          {/* Search input */}
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search users..."
                className="w-full rounded-md border-0 bg-transparent py-1.5 pl-8 pr-8 text-sm focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto p-1">
            {/* All Tasks option */}
            <button
              type="button"
              onClick={() => handleSelect('all')}
              className={cn(
                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                'hover:bg-accent focus:bg-accent focus:outline-none',
                assigneeFilter === 'all' && 'bg-accent font-medium'
              )}
            >
              <span>All Tasks</span>
              {assigneeFilter === 'all' && <CheckCircle2 className="h-4 w-4 text-primary" />}
            </button>

            {/* My Tasks option */}
            {currentUserId && (
              <button
                type="button"
                onClick={() => handleSelect('me')}
                className={cn(
                  'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                  'hover:bg-accent focus:bg-accent focus:outline-none',
                  assigneeFilter === 'me' && 'bg-accent font-medium'
                )}
              >
                <span>My Tasks</span>
                {assigneeFilter === 'me' && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </button>
            )}

            {/* Users section */}
            {filteredUsers.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Users
                </div>
                {filteredUsers.map((user) => {
                  const isSelected = assigneeFilter === user.id;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelect(user.id)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                        'hover:bg-accent focus:bg-accent focus:outline-none',
                        isSelected && 'bg-accent font-medium'
                      )}
                    >
                      <span>{user.name}'s Tasks</span>
                      {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })}
              </>
            )}

            {/* No results */}
            {filteredUsers.length === 0 && searchQuery && (
              <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                No users found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TasksPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(new Set());
  const [addingSubtaskTo, setAddingSubtaskTo] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
  const [selectedHappeningId, setSelectedHappeningId] = useState<string>('');
  const [taskReminders, setTaskReminders] = useState<Array<{ id?: string; offset_hours: number }>>([]);
  
  // Use filter store for persistent filters
  const taskFilters = useFilterStore((state) => state.getTaskFilters());
  const setTaskFilters = useFilterStore((state) => state.setTaskFilters);
  const assigneeFilter = taskFilters.assigneeFilter;
  const searchQuery = taskFilters.searchQuery;
  const hideDone = taskFilters.hideDone;
  const kanbanColumnSorts = taskFilters.kanbanColumnSorts ?? {};

  // Helper functions to update filters
  const handleAssigneeFilterChange = (value: string) => {
    setTaskFilters({ assigneeFilter: value });
  };
  const handleSearchQueryChange = (value: string) => {
    setTaskFilters({ searchQuery: value });
  };
  const handleHideDoneChange = (value: boolean) => {
    setTaskFilters({ hideDone: value });
  };
  const setColumnSort = (status: string, patch: Partial<ColumnSort>) => {
    const current = kanbanColumnSorts[status] ?? { sortBy: 'default' as KanbanSortKey, sortDir: 'asc' as SortDirection };
    const next = { ...current, ...patch };
    if (next.sortBy === 'default') {
      // Drop the entry entirely so the column reverts to native order.
      const { [status]: _removed, ...rest } = kanbanColumnSorts;
      setTaskFilters({ kanbanColumnSorts: rest });
    } else {
      setTaskFilters({ kanbanColumnSorts: { ...kanbanColumnSorts, [status]: next } });
    }
  };
  
  const viewModeRaw = useViewStore((state) => state.getViewMode('tasks'));
  const setViewMode = useViewStore((state) => state.setViewMode);
  const isMobile = useIsMobile();

  // On mobile, only allow 'list' view - fallback calendar/kanban/card to list
  const viewMode: 'list' | 'calendar' | 'kanban' = isMobile
    ? 'list'
    : (viewModeRaw === 'card' ? 'list' : (viewModeRaw as 'list' | 'calendar' | 'kanban'));
  const [calendarVisibleRange, setCalendarVisibleRange] = useState<VisibleRange | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Delete functionality
  const { user } = useAuth();
  const canDelete = !user || user.role === 'owner' || user.role === 'admin';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);
  const [deleteDependencies, setDeleteDependencies] = useState<DependencyCheckResult | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteDependencies, setBulkDeleteDependencies] = useState<Map<string, DependencyCheckResult>>(new Map());
  const [checkingSingleDependencies, setCheckingSingleDependencies] = useState<Set<string>>(new Set());
  const [checkingBulkDependencies, setCheckingBulkDependencies] = useState(false);

  // Compute query filters based on view mode
  const tasksQueryFilter = useMemo(() => {
    if (viewMode === 'calendar' && calendarVisibleRange) {
      // Calendar view: filter by visible date range
      const start = new Date(calendarVisibleRange.start);
      start.setDate(start.getDate() - 1);
      const end = new Date(calendarVisibleRange.end);
      end.setDate(end.getDate() + 2);
      return {
        due_date_gte: start.toISOString(),
        due_date_lte: end.toISOString(),
      };
    }
    if (viewMode === 'list') {
      // List view: use pagination
      return {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };
    }
    // Other views: no filtering
    return undefined;
  }, [viewMode, calendarVisibleRange, currentPage, pageSize]);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks', tasksQueryFilter],
    queryFn: () => listTasks(tasksQueryFilter),
  });

  // Count total tasks for pagination (only needed in list view)
  const { data: totalTasksCount = 0 } = useQuery({
    queryKey: ['tasksCount'],
    queryFn: () => countTasks(),
    enabled: viewMode === 'list',
  });

  const totalPages = Math.ceil(totalTasksCount / pageSize);

  // Reset to page 1 when switching to list view
  useEffect(() => {
    if (viewMode !== 'list') {
      setCurrentPage(1);
    }
  }, [viewMode]);

  // Fetch happenings when we need to show linked happening names or when editing a task
  const { data: happenings = [] } = useQuery({
    queryKey: ['happenings', 'for-task-lookup'],
    queryFn: () => listHappenings(),
    enabled: tasks.some(t => t.related_happening_id) || editingTask !== null, // Fetch if any task has a happening or when editing
    staleTime: 5 * 60 * 1000, // Cache for 5 min
  });

  // Extract unique users from tasks (only users actually assigned to tasks)
  const linkedUsers = useMemo(() => {
    const usersMap = new Map<string, { id: string; name: string }>();
    tasks.forEach(task => {
      if (task.assigned_to_user_id) {
        // We'll need to fetch user names, but for now store the IDs
        // We'll fetch users separately when needed for display
        usersMap.set(task.assigned_to_user_id, {
          id: task.assigned_to_user_id,
          name: '' // Will be populated when we fetch users
        });
      }
    });
    return Array.from(usersMap.values());
  }, [tasks]);

  // Fetch users for display (names) - only fetch users that are actually assigned to tasks
  // Also fetch when editing to have full user list for assignee selection
  const { data: allUsers = [] } = useQuery({
    queryKey: ['users', { is_active: true }],
    queryFn: () => listUsers({ is_active: true }),
    enabled: editingTask !== null || tasks.some(t => t.assigned_to_user_id), // Fetch if any task has a user assigned or when editing
    staleTime: 5 * 60 * 1000, // Cache for 5 min
  });

  // Create a map of user IDs to user objects for lookup
  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    allUsers.forEach(u => map.set(u.id, u));
    return map;
  }, [allUsers]);

  // Populate linked users with names from userMap
  const linkedUsersWithNames = useMemo(() => {
    return linkedUsers
      .map(linked => {
        const user = userMap.get(linked.id);
        return user ? { id: user.id, name: user.name } : null;
      })
      .filter((u): u is { id: string; name: string } => u !== null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [linkedUsers, userMap]);

  // Keep the old userMap for backwards compatibility in other parts of the code
  const users = allUsers;

  // Load reminders when editing a task
  const { data: existingReminders = [], isFetched } = useQuery({
    queryKey: ['task-reminders', editingTask?.id],
    queryFn: () => editingTask ? getTaskReminders(editingTask.id) : Promise.resolve([]),
    enabled: !!editingTask,
  });

  // Keep selected assignee and happening state in sync when opening editor
  useEffect(() => {
    setSelectedAssigneeId(editingTask?.assigned_to_user_id || '');
    setSelectedHappeningId(editingTask?.related_happening_id || '');
  }, [editingTask]);

  // Track which task we've loaded reminders for
  const loadedRemindersForTaskId = useRef<string | null>(null);

  // Reset when task changes
  useEffect(() => {
    if (!editingTask) {
      setTaskReminders([]);
      loadedRemindersForTaskId.current = null;
      return;
    }

    const currentTaskId = editingTask.id;

    // If task changed, reset
    if (loadedRemindersForTaskId.current !== currentTaskId) {
      loadedRemindersForTaskId.current = currentTaskId;
      setTaskReminders([]); // Start empty, will be populated when query loads
    }
  }, [editingTask?.id]);

  // Load reminders when query completes (only if task matches and we haven't manually edited)
  useEffect(() => {
    if (!editingTask || loadedRemindersForTaskId.current !== editingTask.id) {
      return;
    }

    // Only load if query has fetched and we don't have reminders with IDs yet (user hasn't edited)
    if (isFetched) {
      const hasExistingReminderIds = taskReminders.some(r => r.id);
      if (!hasExistingReminderIds) {
        // User hasn't edited, safe to load from server
        setTaskReminders(existingReminders.map(r => ({ id: r.id, offset_hours: r.offset_hours })));
      }
    }
  }, [editingTask?.id, isFetched]); // Only depend on fetch status, not the array itself

  // Server-side search for users for assignee selection
  const searchUsers = useMemo(() => {
    return async (query: string) => {
      const results = await listUsers({ is_active: true, query, limit: 20 });
      return results.map(u => ({ id: u.id, name: u.name, subtitle: u.role }));
    };
  }, []);

  // Server-side search for happenings for linking tasks
  const searchHappenings = useMemo(() => {
    return async (query: string) => {
      const results = await listHappenings({ query });
      // Limit to first 20 results client-side
      return results.slice(0, 20).map(h => ({
        id: h.id,
        name: h.title,
        subtitle: h.start_time ? new Date(h.start_time).toLocaleDateString() : undefined,
      }));
    };
  }, []);

  // Create lookup maps for quick access
  const happeningMap = useMemo(() => {
    const map = new Map<string, Happening>();
    happenings.forEach(h => map.set(h.id, h));
    return map;
  }, [happenings]);

  // Group tasks into parent/child hierarchy
  const { parentTasks, subtaskMap } = useMemo(() => {
    const subtaskMap = new Map<string, Task[]>();
    const parentTasks: Task[] = [];

    tasks.forEach((task) => {
      if (task.parent_task_id) {
        const existing = subtaskMap.get(task.parent_task_id) || [];
        existing.push(task);
        subtaskMap.set(task.parent_task_id, existing);
      } else {
        parentTasks.push(task);
      }
    });

    // Sort subtasks by sort_order
    subtaskMap.forEach((subtasks) => {
      subtasks.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    });

    return { parentTasks, subtaskMap };
  }, [tasks]);

  // Handle deep-link from global search, other pages, or URL parameters (e.g., from email links)
  useEffect(() => {
    // Check URL search parameters first (for email links)
    const searchParams = new URLSearchParams(location.search);
    const urlTaskId = searchParams.get('openEntityId');
    const urlEntityType = searchParams.get('entityType');

    // Also check location state (for internal navigation)
    const state = location.state as { openEntityId?: string; entityType?: string } | null;
    const taskId = urlTaskId || state?.openEntityId;
    const entityType = urlEntityType || state?.entityType;

    if (taskId && entityType === 'task' && tasks.length > 0) {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        // Open the edit dialog for this task
        setEditingTask(task);
        // If it's a subtask, expand the parent
        if (task.parent_task_id) {
          setExpandedTaskIds((prev) => new Set(prev).add(task.parent_task_id!));
        }
        // Clear URL parameters and state so it doesn't re-trigger
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [location.search, location.state, tasks, navigate, location.pathname]);

  // Filter tasks based on search query and assignee filter
  const filteredTasks = useMemo(() => {
    let filtered = parentTasks;

    // Apply hide done filter
    if (hideDone) {
      filtered = filtered.filter((task) => task.status !== 'done');
    }

    // Apply assignee filter
    if (assigneeFilter === 'me' && user?.id) {
      filtered = filtered.filter((task) => task.assigned_to_user_id === user.id);
    } else if (assigneeFilter !== 'all') {
      filtered = filtered.filter((task) => task.assigned_to_user_id === assigneeFilter);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter((task) => {
        // Match parent task
        const parentMatches =
          task.title.toLowerCase().includes(lowerQuery) ||
          task.type.toLowerCase().includes(lowerQuery) ||
          task.status.toLowerCase().includes(lowerQuery) ||
          task.priority.toLowerCase().includes(lowerQuery) ||
          task.description?.toLowerCase().includes(lowerQuery);

        // Or match any subtask
        const subtasks = subtaskMap.get(task.id) || [];
        const subtaskMatches = subtasks.some(
          (st) =>
            st.title.toLowerCase().includes(lowerQuery) ||
            st.type.toLowerCase().includes(lowerQuery) ||
            st.status.toLowerCase().includes(lowerQuery)
        );

        return parentMatches || subtaskMatches;
      });
    }

    return filtered;
  }, [parentTasks, subtaskMap, searchQuery, assigneeFilter, user?.id, hideDone]);

  // Per-column sort comparator. Looked up by status in KanbanBoard so each
  // column can be sorted independently. Comparators are written for ascending;
  // we flip the sign at the end if the user picked descending.
  const sortColumnTasks = useCallback(
    (items: Task[], status: string): Task[] => {
      const cfg = kanbanColumnSorts[status];
      if (!cfg || cfg.sortBy === 'default') return items;
      const priorityRank: Record<TaskPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
      const dir = cfg.sortDir === 'desc' ? -1 : 1;
      const copy = [...items];
      switch (cfg.sortBy) {
        case 'priority':
          copy.sort((a, b) => dir * ((priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99)));
          break;
        case 'due_date':
          // Undated tasks always sink to the bottom regardless of direction.
          copy.sort((a, b) => {
            const aHas = !!a.due_date;
            const bHas = !!b.due_date;
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            if (!aHas && !bHas) return 0;
            return dir * (new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime());
          });
          break;
        case 'title':
          copy.sort((a, b) => dir * a.title.localeCompare(b.title));
          break;
        case 'created':
          copy.sort((a, b) => dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
          break;
      }
      return copy;
    },
    [kanbanColumnSorts],
  );

  const createMutation = useMutation({
    mutationFn: (data: TaskCreate) => createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setShowCreateForm(false);
      setAddingSubtaskTo(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TaskUpdate }) => updateTask(id, data),
    onSuccess: (updatedTask) => {
      console.log('Task updated successfully:', updatedTask);
      // Optimistically update the cache with the returned task data
      queryClient.setQueryData(['tasks', { exclude_done: false }], (oldTasks: Task[] | undefined) => {
        if (!oldTasks) return [updatedTask];
        return oldTasks.map(t => t.id === updatedTask.id ? updatedTask : t);
      });
      // Also invalidate to ensure we get fresh data
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      // Don't close dialog here - let handleEditSubmit handle it after reminders are saved
    },
  });

  // Delete mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setDeleteDialogOpen(false);
      setDeletingTask(null);
      setDeleteDependencies(null);
      setEditingTask(null);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => deleteTasks(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setBulkDeleteDialogOpen(false);
      setSelectedTaskIds(new Set());
      setBulkDeleteDependencies(new Map());
    },
  });

  // Delete handlers
  const handleDeleteClick = async (task: Task, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!canDelete) return;

    setCheckingSingleDependencies((prev) => new Set(prev).add(task.id));
    try {
      setDeletingTask(task);
      const dependencies = await checkTaskDependencies(task.id);
      setDeleteDependencies(dependencies);
      setDeleteDialogOpen(true);
    } finally {
      setCheckingSingleDependencies((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingTask) {
      deleteMutation.mutate(deletingTask.id);
    }
  };

  const handleBulkDeleteClick = async () => {
    if (selectedTaskIds.size === 0 || !canDelete) return;

    setCheckingBulkDependencies(true);
    try {
      const ids = Array.from(selectedTaskIds);
      const dependencies = await checkBulkDependencies('task', ids);
      setBulkDeleteDependencies(dependencies);
      setBulkDeleteDialogOpen(true);
    } finally {
      setCheckingBulkDependencies(false);
    }
  };

  const handleBulkDeleteConfirm = () => {
    if (selectedTaskIds.size > 0) {
      bulkDeleteMutation.mutate(Array.from(selectedTaskIds));
    }
  };

  const toggleTaskSelection = (taskId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTaskIds.size === filteredTasks.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(filteredTasks.map((t) => t.id)));
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingTask) return;
    const formData = new FormData(e.currentTarget);
    const assigneeRole = formData.get('assignee_role') as string;
    // Use selectedAssigneeId directly - it should be set when user selects from dropdown
    const assignedToUserId = selectedAssigneeId && selectedAssigneeId.trim() !== '' ? selectedAssigneeId.trim() : null;
    // Use selectedHappeningId directly - it should be set when user selects from dropdown
    const relatedHappeningId = selectedHappeningId && selectedHappeningId.trim() !== '' ? selectedHappeningId.trim() : null;

    // Convert datetime-local to UTC ISO string
    const dueDateInput = formData.get('due_date') as string | null;
    let dueDateUtc: string | null = null;
    if (dueDateInput) {
      // datetime-local gives "YYYY-MM-DDTHH:mm" in local time
      // Create a Date object in local time, then convert to UTC ISO string
      const localDate = new Date(dueDateInput);
      dueDateUtc = localDate.toISOString();
    }

    const updateData: TaskUpdate = {
      title: formData.get('title') as string,
      type: coerceTaskType(formData.get('type')),
      description: formData.get('description') as string || null,
      status: coerceTaskStatus(formData.get('status')),
      priority: coerceTaskPriority(formData.get('priority')),
      due_date: dueDateUtc,
      assigned_to_user_id: assignedToUserId,
      assignee_role: assigneeRole || null,
      related_happening_id: relatedHappeningId,
    };

    try {
      // Update task first
      await updateMutation.mutateAsync({
        id: editingTask.id,
        data: updateData,
      });

      // Then update reminders
      // Delete all existing reminders
      await deleteTaskReminders(editingTask.id);

      // Create new reminders
      for (const reminder of taskReminders) {
        await createTaskReminder({
          task_id: editingTask.id,
          offset_hours: reminder.offset_hours,
        });
      }

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['task-reminders', editingTask.id] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });

      // Close dialog on success
      setEditingTask(null);
      setTaskReminders([]);
      setSelectedAssigneeId('');
      setSelectedHappeningId('');
    } catch (error) {
      console.error('Failed to update task or reminders:', error);
      alert(`Failed to save changes: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const toggleExpanded = (taskId: string) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const relatedHappeningId = selectedHappeningId && selectedHappeningId.trim() !== '' ? selectedHappeningId.trim() : null;

    // Convert datetime-local to UTC ISO string
    const dueDateInput = formData.get('due_date') as string | null;
    let dueDateUtc: string | null = null;
    if (dueDateInput) {
      // datetime-local gives "YYYY-MM-DDTHH:mm" in local time
      // Create a Date object in local time, then convert to UTC ISO string
      const localDate = new Date(dueDateInput);
      dueDateUtc = localDate.toISOString();
    }

    let newTask: Task | null = null;
    try {
      // Create the task first
      newTask = await createMutation.mutateAsync({
        title: formData.get('title') as string,
        type: coerceTaskType(formData.get('type')),
        description: formData.get('description') as string || null,
        status: coerceTaskStatus(formData.get('status')),
        priority: coerceTaskPriority(formData.get('priority')),
        due_date: dueDateUtc,
        related_happening_id: relatedHappeningId,
      });

      // Try to create reminders, but don't fail the whole operation if this fails
      if (taskReminders.length > 0 && newTask) {
        try {
          for (const reminder of taskReminders) {
            await createTaskReminder({
              task_id: newTask.id,
              offset_hours: reminder.offset_hours,
            });
          }
          // Only invalidate reminder queries if reminders were successfully created
          queryClient.invalidateQueries({ queryKey: ['task-reminders', newTask.id] });
        } catch (reminderError) {
          console.error('Failed to create reminders (task was still created):', reminderError);
          // Show a warning but don't fail the operation
          alert(`Task created successfully, but reminders could not be saved: ${reminderError instanceof Error ? reminderError.message : 'Unknown error'}\n\nYou can add reminders later by editing the task.`);
        }
      }

      // Always invalidate tasks query to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['tasks'] });

      // Reset form state
      setSelectedHappeningId('');
      setTaskReminders([]);
    } catch (error) {
      console.error('Failed to create task:', error);
      alert(`Failed to create task: ${error instanceof Error ? error.message : 'Unknown error'}`);
      // Don't reset form state on error so user can retry
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-badge text-green-badge-foreground';
      case 'in_progress':
        return 'bg-blue-badge text-blue-badge-foreground';
      case 'blocked':
        return 'bg-red-badge text-red-badge-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-badge text-red-badge-foreground';
      case 'high':
        return 'bg-orange-badge text-orange-badge-foreground';
      case 'medium':
        return 'bg-yellow-badge text-yellow-badge-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Check if a task is overdue (past due date and not completed)
  const isTaskOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'done') return false;
    return new Date(task.due_date) < new Date();
  };

  // Group tasks by due date for mobile card view
  const tasksByDueDate = useMemo(() => {
    const groups: Map<string, Task[]> = new Map();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    filteredTasks.forEach((task) => {
      let groupKey: string;

      // Completed tasks go in their own section regardless of due date
      if (task.status === 'done') {
        groupKey = 'completed';
      } else if (!task.due_date) {
        groupKey = 'no-date';
      } else {
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);

        if (dueDate < today) {
          groupKey = 'overdue';
        } else if (dueDate.getTime() === today.getTime()) {
          groupKey = 'today';
        } else if (dueDate.getTime() === tomorrow.getTime()) {
          groupKey = 'tomorrow';
        } else {
          groupKey = dueDate.toISOString().split('T')[0];
        }
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(task);
    });

    // Sort groups: overdue first, then today, tomorrow, future dates, no-date, completed last
    const sortOrder = ['overdue', 'today', 'tomorrow'];
    const sortedEntries = Array.from(groups.entries()).sort(([keyA], [keyB]) => {
      const indexA = sortOrder.indexOf(keyA);
      const indexB = sortOrder.indexOf(keyB);

      // completed and no-date go at the end
      if (keyA === 'completed') return 1;
      if (keyB === 'completed') return -1;
      if (keyA === 'no-date') return 1;
      if (keyB === 'no-date') return -1;
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return keyA.localeCompare(keyB);
    });

    return sortedEntries;
  }, [filteredTasks]);

  const getDateGroupLabel = (key: string): string => {
    if (key === 'overdue') return 'Overdue';
    if (key === 'today') return 'Today';
    if (key === 'tomorrow') return 'Tomorrow';
    if (key === 'no-date') return 'No Due Date';
    if (key === 'completed') return 'Completed';
    // Parse date and format nicely
    const date = new Date(key + 'T00:00:00');
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const renderMobileCardView = () => (
    <div className="space-y-6">
      {tasksByDueDate.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No tasks found</p>
      ) : (
        tasksByDueDate.map(([groupKey, groupTasks]) => (
          <div key={groupKey} className="space-y-3">
            <h3 className={cn(
              "text-sm font-semibold uppercase tracking-wide px-1",
              groupKey === 'overdue' && "text-destructive",
              groupKey === 'completed' && "text-green-600 dark:text-green-500",
              groupKey !== 'overdue' && groupKey !== 'completed' && "text-muted-foreground"
            )}>
              {getDateGroupLabel(groupKey)}
              <span className="ml-2 text-xs font-normal">({groupTasks.length})</span>
            </h3>
            <div className="space-y-2">
              {groupTasks.map((task) => {
                const assignee = task.assigned_to_user_id ? userMap.get(task.assigned_to_user_id) : null;
                const linkedHappening = task.related_happening_id ? happeningMap.get(task.related_happening_id) : null;

                return (
                  <div
                    key={task.id}
                    onClick={() => setEditingTask(task)}
                    className="p-3 rounded-lg border bg-card cursor-pointer hover:border-primary transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{task.title}</p>
                        {task.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                            {task.description}
                          </p>
                        )}
                      </div>
                      <span
                        className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${getStatusColor(task.status)}`}
                      >
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      {assignee && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <UserIcon className="h-3 w-3" />
                          {assignee.name}
                        </span>
                      )}
                      {linkedHappening && (
                        <span className="inline-flex items-center gap-1 text-xs text-primary">
                          <Calendar className="h-3 w-3" />
                          {linkedHappening.title}
                        </span>
                      )}
                      {task.due_date && groupKey !== 'overdue' && groupKey !== 'today' && groupKey !== 'tomorrow' && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {new Date(task.due_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className={cn('space-y-8', viewMode === 'kanban' && 'w-full')} style={viewMode === 'kanban' ? { maxWidth: '100%', overflowX: 'hidden' } : undefined}>
      <div className="flex items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Tasks</h1>
          <p className="hidden sm:block text-muted-foreground">Manage work assignments for all internal people.</p>
        </div>
        <Button onClick={() => setShowCreateForm(!showCreateForm)} size={isMobile ? 'sm' : 'default'}>
          <Plus className="mr-2 h-4 w-4" />
          {isMobile ? 'Add' : 'Add Task'}
        </Button>
      </div>

      {/* Bulk actions bar */}
      {selectedTaskIds.size > 0 && canDelete && (
        <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
          <span className="text-sm font-medium">
            {selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedTaskIds(new Set())}
              disabled={checkingBulkDependencies}
            >
              Clear selection
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDeleteClick}
              disabled={checkingBulkDependencies}
            >
              {checkingBulkDependencies ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Checking dependencies...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete {selectedTaskIds.size}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className={cn('flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4', viewMode === 'kanban' && 'sticky top-0 z-20 bg-background pb-4 border-b mb-4')}>
        <SearchFilter
          value={searchQuery}
          onChange={handleSearchQueryChange}
          placeholder={isMobile ? "Search tasks..." : "Search tasks by title, type, status, or priority..."}
          className="w-full sm:max-w-sm"
        />
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <AssigneeFilterDropdown
            assigneeFilter={assigneeFilter}
            onFilterChange={handleAssigneeFilterChange}
            linkedUsers={linkedUsersWithNames}
            currentUserId={user?.id}
          />
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideDone}
              onChange={(e) => handleHideDoneChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Hide done
          </label>
          {!isMobile && (
            <ViewToggle
              view={viewMode}
              onViewChange={(mode) => setViewMode('tasks', mode)}
              availableViews={['list', 'calendar', 'kanban']}
            />
          )}
        </div>
        {(searchQuery || hideDone || assigneeFilter !== 'all') && (
          <span className="text-sm text-muted-foreground">
            {filteredTasks.length} of {tasks.length} tasks
            {hideDone && ' (done hidden)'}
          </span>
        )}
      </div>

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Task</CardTitle>
            <CardDescription>Add a new task or work assignment</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Title</label>
                <input
                  name="title"
                  required
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  placeholder="Book artist for next show"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  name="description"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <select
                    name="type"
                    required
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    <option value="booking">Booking</option>
                    <option value="preparation">Preparation</option>
                    <option value="follow_up">Follow Up</option>
                    <option value="technical">Technical</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <select
                    name="priority"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <select
                  name="status"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="blocked">Blocked</option>
                  <option value="done">Done</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Due Date</label>
                <input
                  name="due_date"
                  type="datetime-local"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                />
              </div>
              <div className="rounded-lg border border-input bg-muted/30 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Email Reminders</label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Configure multiple reminders for this task
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {taskReminders.map((reminder, index) => {
                    const offsetDesc = reminder.offset_hours === 0
                      ? 'On due date'
                      : reminder.offset_hours < 0
                        ? `${Math.abs(reminder.offset_hours)} hours before`
                        : `${reminder.offset_hours} hours after`;

                    return (
                      <div key={index} className="flex items-center gap-2 p-2 bg-background rounded border">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 text-sm">{offsetDesc}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTaskReminders(taskReminders.filter((_, i) => i !== index));
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}

                  {taskReminders.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      No reminders configured. Click "Add Reminder" to add one.
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <select
                    id="new-reminder-offset-create"
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    defaultValue="0"
                  >
                    <option value="0">On due date</option>
                    <option value="-1">1 hour before</option>
                    <option value="-3">3 hours before</option>
                    <option value="-6">6 hours before</option>
                    <option value="-12">12 hours before</option>
                    <option value="-24">24 hours before (1 day)</option>
                    <option value="-48">48 hours before (2 days)</option>
                    <option value="-72">72 hours before (3 days)</option>
                    <option value="-168">1 week before (7 days)</option>
                    <option value="1">1 hour after</option>
                    <option value="24">24 hours after (1 day)</option>
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const select = document.getElementById('new-reminder-offset-create') as HTMLSelectElement;
                      const offsetHours = parseInt(select.value, 10);
                      // Check if this offset already exists
                      if (!taskReminders.some(r => r.offset_hours === offsetHours)) {
                        setTaskReminders([...taskReminders, { offset_hours: offsetHours }]);
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Reminder
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Related Happening</label>
                <div className="mt-1">
                  <AsyncSearchableSelect
                    searchFn={searchHappenings}
                    selectedIds={selectedHappeningId ? [selectedHappeningId] : []}
                    onSelect={(option) => {
                      setSelectedHappeningId(option.id);
                    }}
                    placeholder="Search happenings to link..."
                    searchPlaceholder="Type to search happenings..."
                    showSelectedInList={true}
                    initialOptions={selectedHappeningId && happeningMap.has(selectedHappeningId)
                      ? [{
                        id: selectedHappeningId,
                        name: happeningMap.get(selectedHappeningId)!.title,
                        subtitle: happeningMap.get(selectedHappeningId)!.start_time
                          ? new Date(happeningMap.get(selectedHappeningId)!.start_time!).toLocaleDateString()
                          : undefined,
                      }]
                      : undefined}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Link this task to a specific happening (optional)
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMutation.isPending}>
                  Create
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {viewMode === 'calendar' ? (
        // Calendar view: always render to preserve navigation state during loading
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <p>Loading...</p>
            </div>
          )}
          <TasksCalendar
            tasks={filteredTasks}
            userMap={userMap}
            onTaskClick={setEditingTask}
            getStatusColor={getStatusColor}
            getPriorityColor={getPriorityColor}
            onVisibleRangeChange={setCalendarVisibleRange}
          />
        </div>
      ) : isLoading ? (
        <p>Loading...</p>
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          items={filteredTasks}
          sortColumnItems={sortColumnTasks}
          renderColumnHeaderExtra={(status) => (
            <ColumnSortControl
              sort={kanbanColumnSorts[status]}
              onChange={(patch) => setColumnSort(status, patch)}
            />
          )}
          statuses={TASK_STATUSES}
          statusLabels={{
            todo: 'To Do',
            in_progress: 'In Progress',
            blocked: 'Blocked',
            done: 'Done',
          }}
          onItemClick={setEditingTask}
          onStatusChange={(taskId, newStatus) => {
            updateMutation.mutate({
              id: taskId,
              data: { status: newStatus as TaskStatus },
            });
          }}
          renderItem={(task) => {
            const assignee = task.assigned_to_user_id ? userMap.get(task.assigned_to_user_id) : null;
            return (
              <div className="space-y-2">
                <div className="font-medium text-sm">{task.title}</div>
                {task.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {task.description}
                  </p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  {assignee && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <UserIcon className="h-3 w-3" />
                      {assignee.name}
                    </span>
                  )}
                  {task.due_date && (
                    <span className={cn(
                      "inline-flex items-center gap-1 text-xs",
                      isTaskOverdue(task) ? "text-destructive font-medium" : "text-muted-foreground"
                    )}>
                      {isTaskOverdue(task) ? <AlertTriangle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
                      {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            );
          }}
          emptyMessage="No tasks"
        />
      ) : isMobile ? (
        renderMobileCardView()
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {canDelete && (
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.size === filteredTasks.length && filteredTasks.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </TableHead>
                  )}
                  <TableHead className="hidden sm:table-cell"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Linked To</TableHead>
                  <TableHead className="hidden md:table-cell">Assignee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Priority</TableHead>
                  <TableHead className="hidden sm:table-cell">Due Date</TableHead>
                  {canDelete && <TableHead className="w-12">Actions</TableHead>}
                </TableRow>
              </TableHeader>
            <TableBody>
              {filteredTasks.flatMap((task) => {
                const subtasks = subtaskMap.get(task.id) || [];
                const hasSubtasks = subtasks.length > 0;
                const isExpanded = expandedTaskIds.has(task.id);
                const linkedHappening = task.related_happening_id ? happeningMap.get(task.related_happening_id) : null;
                const assignee = task.assigned_to_user_id ? userMap.get(task.assigned_to_user_id) : null;

                // Debug logging for assignee lookup
                if (task.assigned_to_user_id && !assignee) {
                  console.warn('Task has assigned_to_user_id but user not found in userMap:', {
                    taskId: task.id,
                    taskTitle: task.title,
                    assigned_to_user_id: task.assigned_to_user_id,
                    userMapSize: userMap.size,
                    userMapKeys: Array.from(userMap.keys()),
                    usersArrayLength: users.length,
                  });
                }

                const rows = [];

                // Parent task row
                rows.push(
                  <TableRow key={task.id} className="group cursor-pointer hover:bg-muted/50" onClick={() => setEditingTask(task)}>
                    {canDelete && (
                      <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.has(task.id)}
                          onChange={() => toggleTaskSelection(task.id)}
                          className="h-4 w-4 rounded border-gray-300"
                          disabled={checkingSingleDependencies.has(task.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell className="hidden sm:table-cell w-8" onClick={(e) => e.stopPropagation()}>
                      {hasSubtasks && (
                        <button
                          onClick={() => toggleExpanded(task.id)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div>
                        <p>{task.title}</p>
                        {task.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {task.description}
                          </p>
                        )}
                        {hasSubtasks && (
                          <p className="text-xs text-muted-foreground">
                            {subtasks.filter((s) => s.status === 'done').length}/{subtasks.length} subtasks
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {linkedHappening ? (
                        <span className="inline-flex items-center gap-1 text-xs text-primary">
                          <Calendar className="h-3 w-3" />
                          {linkedHappening.title}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {assignee ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <UserIcon className="h-3 w-3" />
                          {assignee.name}
                        </span>
                      ) : task.assignee_role ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          {task.assignee_role.replace(/_/g, ' ')}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${getStatusColor(task.status)}`}
                      >
                        {task.status.replace('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${getPriorityColor(task.priority)}`}
                      >
                        {task.priority}
                      </span>
                    </TableCell>
                    <TableCell className={cn(
                      "hidden sm:table-cell text-xs",
                      isTaskOverdue(task) ? "text-destructive font-medium" : "text-muted-foreground"
                    )}>
                      {task.due_date ? (
                        <span className="inline-flex items-center gap-1">
                          {isTaskOverdue(task) && <AlertTriangle className="h-3 w-3" />}
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      ) : '—'}
                    </TableCell>
                    {canDelete && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteClick(task, e)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          disabled={checkingSingleDependencies.has(task.id)}
                          title={checkingSingleDependencies.has(task.id) ? 'Checking dependencies...' : 'Delete'}
                        >
                          {checkingSingleDependencies.has(task.id) ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );

                // Subtask rows (if expanded)
                if (isExpanded && hasSubtasks) {
                  subtasks.forEach((subtask) => {
                    const subtaskAssignee = subtask.assigned_to_user_id ? userMap.get(subtask.assigned_to_user_id) : null;
                    rows.push(
                      <TableRow key={subtask.id} className="bg-muted/30 cursor-pointer hover:bg-muted/50" onClick={() => setEditingTask(subtask)}>
                        {canDelete && (
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedTaskIds.has(subtask.id)}
                              onChange={() => toggleTaskSelection(subtask.id)}
                              className="h-4 w-4 rounded border-gray-300"
                              disabled={checkingSingleDependencies.has(subtask.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell className="hidden sm:table-cell"></TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2 pl-4">
                            <CornerDownRight className="h-3 w-3 text-muted-foreground" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateMutation.mutate({
                                  id: subtask.id,
                                  data: { status: subtask.status === 'done' ? 'todo' : 'done' },
                                });
                              }}
                              className={`h-4 w-4 rounded border flex items-center justify-center ${subtask.status === 'done'
                                ? 'border-success bg-success text-white'
                                : 'border-muted-foreground'
                                }`}
                            >
                              {subtask.status === 'done' && <CheckCircle2 className="h-3 w-3" />}
                            </button>
                            <span
                              className={
                                subtask.status === 'done' ? 'text-muted-foreground line-through' : ''
                              }
                            >
                              {subtask.title}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-xs">—</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {subtaskAssignee ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                              <UserIcon className="h-3 w-3" />
                              {subtaskAssignee.name}
                            </span>
                          ) : subtask.assignee_role ? (
                            <span className="text-xs text-amber-600">{subtask.assignee_role.replace(/_/g, ' ')}</span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${getStatusColor(subtask.status)}`}
                          >
                            {subtask.status.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${getPriorityColor(subtask.priority)}`}
                          >
                            {subtask.priority}
                          </span>
                        </TableCell>
                        <TableCell className={cn(
                          "hidden sm:table-cell text-xs",
                          isTaskOverdue(subtask) ? "text-destructive font-medium" : "text-muted-foreground"
                        )}>
                          {subtask.due_date ? (
                            <span className="inline-flex items-center gap-1">
                              {isTaskOverdue(subtask) && <AlertTriangle className="h-3 w-3" />}
                              {new Date(subtask.due_date).toLocaleDateString()}
                            </span>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  });
                }

                return rows;
              })}
            </TableBody>
            </Table>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalTasksCount)} of {totalTasksCount} tasks
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Task Dialog */}
      <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
        <DialogContent size="large" className="flex flex-col max-h-[90vh] overflow-hidden p-0">
          {editingTask && (
            <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 min-h-0">
              {/* Sticky Header */}
              <div className="sticky top-0 z-30 px-6 pt-6 pb-4 bg-background border-b shadow-lg" style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'hsl(var(--background))',
              }}>
                <DialogHeader>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                      {getTaskTypeLabel(editingTask.type)}
                    </span>
                    Task
                  </div>
                  <DialogTitle>Edit Task</DialogTitle>
                  <DialogDescription>
                    Update task details and status.
                  </DialogDescription>
                </DialogHeader>

                {/* Quick status and priority controls */}
                <div className="mt-4 pb-3">
                  <div className="flex items-center gap-3">
                    {/* Status dropdown */}
                    <div className="flex-1">
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                      <select
                        name="status"
                        defaultValue={editingTask.status}
                        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium"
                      >
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="blocked">Blocked</option>
                        <option value="done">Done</option>
                      </select>
                    </div>

                    {/* Priority dropdown */}
                    <div className="flex-1">
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Priority</label>
                      <select
                        name="priority"
                        defaultValue={editingTask.priority}
                        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    {/* Related happening info (if exists) */}
                    {editingTask.related_happening_id && (() => {
                      const linkedHappening = happeningMap.get(editingTask.related_happening_id);
                      return linkedHappening ? (
                        <div className="flex-1">
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Happening</label>
                          <button
                            type="button"
                            className="w-full rounded-md border px-3 py-1.5 text-xs text-left hover:bg-accent transition-colors overflow-hidden"
                            onClick={() => {
                              navigate(`/happenings?id=${editingTask.related_happening_id}`);
                            }}
                            title="Click to view happening"
                          >
                            <div className="font-medium truncate">{linkedHappening.title}</div>
                            {linkedHappening.start_time && (
                              <div className="text-muted-foreground mt-0.5">
                                {new Date(linkedHappening.start_time).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                          </button>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

              {/* Scrollable Form Body */}
              <div className="px-6 py-6 space-y-4 flex-1 min-h-0 overflow-y-auto">
              <div>
                <label className="text-sm font-medium">Title</label>
                <input
                  name="title"
                  required
                  defaultValue={editingTask.title}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  name="description"
                  defaultValue={editingTask.description || ''}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <select
                  name="type"
                  defaultValue={editingTask.type}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                >
                  <option value="booking">Booking</option>
                  <option value="preparation">Preparation</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Due Date</label>
                <input
                  name="due_date"
                  type="datetime-local"
                  defaultValue={
                    editingTask.due_date
                      ? (() => {
                        // Convert UTC date from database to local time for datetime-local input
                        const utcDate = new Date(editingTask.due_date);
                        // datetime-local expects format: YYYY-MM-DDTHH:mm
                        // Use local time components (not UTC)
                        const year = utcDate.getFullYear();
                        const month = String(utcDate.getMonth() + 1).padStart(2, '0');
                        const day = String(utcDate.getDate()).padStart(2, '0');
                        const hours = String(utcDate.getHours()).padStart(2, '0');
                        const minutes = String(utcDate.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}`;
                      })()
                      : ''
                  }
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                />
              </div>
              <div className="rounded-lg border border-input bg-muted/30 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Email Reminders</label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Configure multiple reminders for this task
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {taskReminders.map((reminder, index) => {
                    const offsetDesc = reminder.offset_hours === 0
                      ? 'On due date'
                      : reminder.offset_hours < 0
                        ? `${Math.abs(reminder.offset_hours)} hours before`
                        : `${reminder.offset_hours} hours after`;

                    return (
                      <div key={index} className="flex items-center gap-2 p-2 bg-background rounded border">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 text-sm">{offsetDesc}</span>
                        {existingReminders.find(r => r.id === reminder.id)?.sent_at && (
                          <span className="text-xs text-muted-foreground">
                            Sent: {new Date(existingReminders.find(r => r.id === reminder.id)!.sent_at!).toLocaleString()}
                          </span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTaskReminders(taskReminders.filter((_, i) => i !== index));
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}

                  {taskReminders.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      No reminders configured. Click "Add Reminder" to add one.
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <select
                    id="new-reminder-offset"
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    defaultValue="0"
                  >
                    <option value="0">On due date</option>
                    <option value="-1">1 hour before</option>
                    <option value="-3">3 hours before</option>
                    <option value="-6">6 hours before</option>
                    <option value="-12">12 hours before</option>
                    <option value="-24">24 hours before (1 day)</option>
                    <option value="-48">48 hours before (2 days)</option>
                    <option value="-72">72 hours before (3 days)</option>
                    <option value="-168">1 week before (7 days)</option>
                    <option value="1">1 hour after</option>
                    <option value="24">24 hours after (1 day)</option>
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const select = document.getElementById('new-reminder-offset') as HTMLSelectElement;
                      const offsetHours = parseInt(select.value, 10);
                      // Check if this offset already exists
                      if (!taskReminders.some(r => r.offset_hours === offsetHours)) {
                        setTaskReminders([...taskReminders, { offset_hours: offsetHours }]);
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Reminder
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Related Happening</label>
                <div className="mt-1">
                  <AsyncSearchableSelect
                    searchFn={searchHappenings}
                    selectedIds={selectedHappeningId ? [selectedHappeningId] : []}
                    onSelect={(option) => {
                      setSelectedHappeningId(option.id);
                    }}
                    placeholder="Search happenings to link..."
                    searchPlaceholder="Type to search happenings..."
                    showSelectedInList={true}
                    initialOptions={selectedHappeningId && happeningMap.has(selectedHappeningId)
                      ? [{
                        id: selectedHappeningId,
                        name: happeningMap.get(selectedHappeningId)!.title,
                        subtitle: happeningMap.get(selectedHappeningId)!.start_time
                          ? new Date(happeningMap.get(selectedHappeningId)!.start_time!).toLocaleDateString()
                          : undefined,
                      }]
                      : undefined}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Link this task to a specific happening (optional)
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Role (for auto-assign)</label>
                  <input
                    name="assignee_role"
                    defaultValue={editingTask.assignee_role || ''}
                    placeholder="e.g. social_media, producer"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    When a participant with this role is added, task auto-assigns
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Assignee</label>
                  <div className="mt-1">
                    <AsyncSearchableSelect
                      searchFn={searchUsers}
                      selectedIds={selectedAssigneeId ? [selectedAssigneeId] : []}
                      onSelect={(option) => {
                        console.log('Assignee selected in dropdown:', option);
                        setSelectedAssigneeId(option.id);
                      }}
                      placeholder="Search staff to assign..."
                      searchPlaceholder="Type to search staff..."
                      showSelectedInList={true}
                      initialOptions={selectedAssigneeId && userMap.has(selectedAssigneeId)
                        ? [{
                          id: selectedAssigneeId,
                          name: userMap.get(selectedAssigneeId)!.name,
                          subtitle: userMap.get(selectedAssigneeId)!.role,
                        }]
                        : undefined}
                    />
                  </div>
                </div>
              </div>
              </div>

              {canDelete && (
                <div className="px-6">
                  <DangerZone
                    entityLabel="task"
                    onDelete={() => handleDeleteClick(editingTask)}
                    disabled={deleteMutation.isPending}
                  />
                </div>
              )}

              {/* Sticky Footer */}
              <DialogFooter className="sticky bottom-0 left-0 right-0 bg-background border-t px-6 pb-6 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditingTask(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deletingTask && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          entityName={deletingTask.title}
          entityType="task"
          dependencies={deleteDependencies || undefined}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {selectedTaskIds.size > 0 && (
        <DeleteConfirmationDialog
          open={bulkDeleteDialogOpen}
          onOpenChange={setBulkDeleteDialogOpen}
          onConfirm={handleBulkDeleteConfirm}
          entityName=""
          entityType="task"
          entityCount={selectedTaskIds.size}
          dependencies={(() => {
            // Aggregate dependencies from all selected tasks
            const allDeps: DependencyCheckResult = {
              hasDependencies: false,
              dependencies: [],
            };
            const depMap = new Map<string, number>();
            const depItems = new Map<string, Array<{ id: string; name: string }>>();

            bulkDeleteDependencies.forEach((result) => {
              if (result.hasDependencies) {
                allDeps.hasDependencies = true;
                result.dependencies.forEach((dep) => {
                  const key = dep.type;
                  depMap.set(key, (depMap.get(key) || 0) + dep.count);
                  if (!depItems.has(key)) {
                    depItems.set(key, []);
                  }
                  depItems.get(key)!.push(...dep.items);
                });
              }
            });

            depMap.forEach((count, type) => {
              allDeps.dependencies.push({
                type,
                count,
                items: depItems.get(type) || [],
              });
            });

            return allDeps;
          })()}
          isDeleting={bulkDeleteMutation.isPending}
        />
      )}
    </div>
  );
}
